import styled, { css, keyframes } from 'styled-components';
import { Link } from 'react-router-dom';

import { GlowCard, Text } from '../../../../components';
import { cardEnterAt } from '../../../../theme/mixins';
import {
  ANIM,
  COLORS,
  HOME,
  SPACE,
  REDUCED_MOTION,
  WEBSITE_HUB,
  WEBSITE_HUB_CHIP,
  type WebsiteCardTone,
} from '../../../../theme/tokens';

/** The icon tile: 44px, holding a 24px glyph (half the earlier 88px tile and 48px glyph, as the owner asked). */
export const TILE_PX = 44;
export const TILE_RADIUS_PX = 11;
export const ICON_PX = 24;
/** Home's circled arrow, at Home's size (Gauge and the acquisition channel cards). */
const ARROW_PX = 24;
const THUMB_PX = 28;
const SWATCH_PX = 14;
const CHIP_PX = 24;
/**
 * The body slot's height: its tallest content, a context line with its thumbnail and, under the line, the swatches.
 * Every card reserves it (and the chip's) from its first paint, skeleton and all, so nothing that loads into a card
 * changes its height (or, through the row, the cards around it).
 */
const BODY_SLOT = `calc(${THUMB_PX}px + ${SPACE[8]} + ${SWATCH_PX}px)`;

/**
 * The title, stretched over the whole card: its `::after` covers the card (the root is the positioned ancestor), so
 * a click anywhere on the card's static content opens the area, with no interactive element nested in another. The
 * link itself stays unpositioned for that reason. Its keyboard focus rings the whole card (see Root), so it draws no
 * outline of its own.
 */
export const TitleLink = styled(Link)`
  color: ${WEBSITE_HUB.title};
  text-decoration: none;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
  }

  &:hover,
  &:focus {
    color: ${WEBSITE_HUB.title};
    text-decoration: none;
  }

  &:focus-visible {
    outline: none;
  }
`;

/**
 * One Website area's card: a GlowCard (Home's score gauge card: its base, hairline, radius, the glow in the area's
 * colour from the top-left, and the hover wash), laid out as a column: the header (the icon tile, with the title,
 * description, chip and body beside it), then the arrow's row pinned to the bottom, so cards in a row share a height
 * and their arrows a baseline.
 */
export const Root = styled(GlowCard)<{ $enterIndex: number }>`
  /* Staggered entrance: the n-th card starts n stagger steps after the first, held hidden until its turn. */
  ${(p) => cardEnterAt(p.$enterIndex)}
  display: flex;
  flex-direction: column;
  gap: ${SPACE[20]};
  height: 100%;
  min-width: 0;
  padding: ${SPACE[20]};

  /* The stretched title link is the card's own control: its keyboard focus rings the whole card. */
  &:focus-within:has(${TitleLink}:focus-visible) {
    outline: 2px solid ${COLORS.text};
    outline-offset: 2px;
  }
`;

/** The tile and, beside it, the card's words. */
export const Header = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${SPACE[20]};
  min-width: 0;
`;

/** The title, description and chip, stacked beside the tile. */
export const Words = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
  min-width: 0;
`;

/** The icon tile: a neutral surface (the same in every area; the area's colour is the card's glow). */
export const Tile = styled.div`
  flex: 0 0 auto;
  width: ${TILE_PX}px;
  height: ${TILE_PX}px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${TILE_RADIUS_PX}px;
  background: ${COLORS.surfaceMuted};
`;

export const Heading = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 500;
  line-height: 1.2;
`;

export const Description = styled(Text)`
  margin: 0;
  font-size: 14px;
  font-weight: 300;
  line-height: 1.45;
  color: ${COLORS.textMuted};
`;

/** The chip's place under the description, held at the chip's height whether or not one arrives. */
export const ChipSlot = styled.div`
  display: flex;
  min-height: ${CHIP_PX}px;
`;

export const Chip = styled.span<{ $tone: WebsiteCardTone }>`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  height: ${CHIP_PX}px;
  padding: 0 ${SPACE[8]};
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  color: ${(p) => WEBSITE_HUB_CHIP[p.$tone].ink};
  background: ${(p) => WEBSITE_HUB_CHIP[p.$tone].bg};
`;

/** The skeleton leaving and the loaded details arriving: a fade, and a short rise (a transform, so no layout moves). */
const skeletonOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const detailsIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(${SPACE[4]});
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

/**
 * The card's body, in a slot of fixed height ({@link BODY_SLOT}) along the bottom, its content settled on the arrow's
 * baseline. The skeleton and the details share its one grid cell, so while the details rise in the leaving skeleton
 * fades out beneath them.
 */
export const Status = styled.div`
  flex: 1 1 auto;
  display: grid;
  align-content: end;
  min-width: 0;
  min-height: ${BODY_SLOT};

  & > * {
    grid-area: 1 / 1;
  }
`;

/** The skeleton's layer: fading out once the details are in (reduced motion: gone at once). */
export const SkeletonLayer = styled.div<{ $leaving: boolean }>`
  min-width: 0;
  pointer-events: none;

  ${(p) =>
    p.$leaving &&
    css`
      animation: ${skeletonOut} ${ANIM.HUB_CARD_SKELETON_OUT_MS}ms ${ANIM.EASE} both;

      ${REDUCED_MOTION} {
        animation-duration: ${ANIM.HUB_CARD_REDUCED_MS}ms;
      }
    `}
`;

/** The loaded chip and body, rising in when they replace the skeleton (reduced motion: shown at once, the 1ms
 *  animation still ending, so the skeleton's layer is dropped). A card loaded from its first render shows them still. */
export const Details = styled.div<{ $reveal: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
  min-width: 0;

  ${(p) =>
    p.$reveal &&
    css`
      animation: ${detailsIn} ${ANIM.HUB_CARD_REVEAL_MS}ms ${ANIM.EASE} both;

      ${REDUCED_MOTION} {
        animation-duration: ${ANIM.HUB_CARD_REDUCED_MS}ms;
      }
    `}
`;

export const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[4]};
  min-width: 0;
`;

export const Line = styled.p`
  margin: 0;
  min-width: 0;
  font-size: 14px;
  line-height: 1.4;
  color: ${COLORS.text};
`;

/**
 * A context line with its artwork: the thumbnail beside the line, and the swatches under the line, starting where its
 * text starts (not under the thumbnail). With no swatches there is no second row, so the line sits on the card's
 * bottom edge beside the arrow.
 */
export const ContextRow = styled.div<{ $withThumb: boolean; $withSwatches: boolean }>`
  display: grid;
  grid-template-columns: ${(p) => (p.$withThumb ? 'auto minmax(0, 1fr)' : 'minmax(0, 1fr)')};
  grid-template-areas: ${(p) =>
    p.$withThumb
      ? p.$withSwatches
        ? '"thumb line" ". swatches"'
        : '"thumb line"'
      : p.$withSwatches
        ? '"line" "swatches"'
        : '"line"'};
  align-items: center;
  gap: ${SPACE[8]};
  min-width: 0;
`;

export const ContextLine = styled(Line)`
  grid-area: line;
`;

/** The artwork's thumbnail, at a fixed size (its image never moves the line), fading in once decoded over its own
 *  placeholder tint. */
export const Thumb = styled.img<{ $loaded: boolean }>`
  grid-area: thumb;
  flex: 0 0 auto;
  width: ${THUMB_PX}px;
  height: ${THUMB_PX}px;
  border-radius: 8px;
  object-fit: cover;
  background: ${COLORS.surfaceFainter};
  opacity: ${(p) => (p.$loaded ? 1 : 0)};
  transition: opacity ${ANIM.HUB_CARD_THUMB_FADE_MS}ms ${ANIM.EASE};

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

export const Swatches = styled.span`
  grid-area: swatches;
  justify-self: start;
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[4]};
`;

/** One palette colour. The colour is the style's own data (an API value the card has checked is a plain hex), not a
 *  design colour. */
export const Swatch = styled.span<{ $hex: string }>`
  flex: 0 0 auto;
  width: ${SWATCH_PX}px;
  height: ${SWATCH_PX}px;
  box-sizing: border-box;
  border-radius: 50%;
  border: 1px solid ${WEBSITE_HUB.swatchRing};
  background-color: ${(p) => p.$hex};
`;

/** The bottom row, pinned to the bottom: the body, starting where the words start (past the tile), and the arrow at the
 *  right. It lets clicks through, so it opens the area like the rest of the card. */
export const Footer = styled.div`
  margin-top: auto;
  display: flex;
  align-items: flex-end;
  gap: ${SPACE[16]};
  padding-left: calc(${TILE_PX}px + ${SPACE[20]});
  pointer-events: none;

  /* Optical, off the scale on purpose: a lone text line (20px) is lifted 2px so it centres on the 24px arrow. */
  &:not(:has(${Thumb}, ${Swatches})) > ${Status} {
    padding-bottom: ${SPACE[2]};
  }
`;

/** Home's circled arrow, exactly as Home draws it (Gauge's Chev): a 24px circle in Home's light ink that nudges 2px
 *  right while the card is hovered. */
export const Arrow = styled.span`
  flex: 0 0 auto;
  margin-left: auto;
  width: ${ARROW_PX}px;
  height: ${ARROW_PX}px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  color: ${HOME.t50};
  transition: transform ${ANIM.INTERACT_MS}ms ${HOME.ease};

  ${Root}:hover & {
    transform: translateX(2px);
  }

  /* Optical, off the scale on purpose: beside a lone thumbnail row (28px) the arrow is lifted 2px to centre on it. */
  ${Footer}:has(${Thumb}):not(:has(${Swatches})) > & {
    margin-bottom: ${SPACE[2]};
  }
`;
